import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { APPROVAL_COLOR, APPROVAL_LABEL, type ApprovalState } from '../api/changeManagement'
import { maintenanceCalendarQuery, type CalendarChange, type CalendarWindow, type Conflict } from '../api/maintenance'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { CmdbViews } from '../cmdb/CmdbViews'
import { AdmPageHead } from '../softsee/adm/AdmPageHead'
import { Forbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { MaintenanceShapes, SCOPE_ICON } from './MaintenanceShapes'
import { WindowFormModal } from './WindowFormModal'

/**
 * `Maintenance::calendar` - aceleasi ferestre ca o luna, la /app/maintenance/calendar.
 *
 * O lista raspunde "ce e planificat"; nu raspunde "ce altceva se intampla in
 * noaptea aia", si aia e intrebarea pentru care se intruneste un CAB. Doua
 * schimbari pe acelasi element in acelasi timp sunt invizibile intr-un tabel
 * sortat dupa data. `views/version-3/inventoree/maintenance/calendar.php`.
 */

const STYLES = ['css/adm.css']
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + n)
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + by, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/** Perechile de ciocniri, fiecare o singura data. */
export function pairs(windows: CalendarWindow[], conflicts: Record<string, Conflict[]>): { one: CalendarWindow; other: Conflict }[] {
  const seen = new Set<string>()
  const out: { one: CalendarWindow; other: Conflict }[] = []
  for (const w of windows) {
    for (const c of conflicts[String(w.ID)] ?? []) {
      const key = [Math.min(w.ID, c.id), Math.max(w.ID, c.id)].join('-')
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ one: w, other: c })
    }
  }
  return out
}

export function MaintenanceCalendarPage() {
  const shell = useQuery(shellQuery(moduleOf('/maintenance')))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const month = new URLSearchParams(window.location.search).get('month') ?? ''
  const page = useQuery(maintenanceCalendarQuery(month))
  const [form, setForm] = useState<{ id: number; day: string } | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data
  const today = new Date().toISOString().slice(0, 10)
  const byId = new Map(data.windows.map((w) => [w.ID, w]))
  const byDay = new Map<string, CalendarWindow[]>()
  const gridEnd = addDays(data.gridStart, data.cells - 1)
  for (const w of data.windows) {
    let from = w.starts_at_full.slice(0, 10)
    let to = w.ends_at_full.slice(0, 10)
    if (from < data.gridStart) from = data.gridStart
    if (to > gridEnd) to = gridEnd
    for (let d = from; d <= to; d = addDays(d, 1)) {
      byDay.set(d, [...(byDay.get(d) ?? []), w])
    }
  }
  /* Change-urile planificate, asezate pe zile la fel ca ferestrele. Un raspuns
     de dinaintea lor (fara cheie) se citeste ca "niciun change". */
  const changes = data.changes ?? []
  const changeClashes = data.changeClashes ?? []
  const changesByDay = new Map<string, CalendarChange[]>()
  for (const c of changes) {
    let from = c.starts_at_full.slice(0, 10)
    let to = c.ends_at_full.slice(0, 10)
    if (from < data.gridStart) from = data.gridStart
    if (to > gridEnd) to = gridEnd
    for (let d = from; d <= to; d = addDays(d, 1)) {
      changesByDay.set(d, [...(changesByDay.get(d) ?? []), c])
    }
  }
  const changeById = new Map(changes.map((c) => [c.ticket_id, c]))
  const clashingChanges = new Set(changeClashes.flatMap((c) => (c.kind === 'change' ? [c.change, c.id] : [c.change])))
  const clashes = pairs(data.windows, data.conflicts)
  const startingHere = data.windows.filter((w) => w.starts_at_full.slice(0, 7) === data.month).length
  const changesHere = changes.filter((c) => c.starts_at_full.slice(0, 7) === data.month).length
  const dayDate = data.month === today.slice(0, 7) ? today : `${data.month}-01`

  return (
    <Content>
      {/* Banda celor patru vederi, primul copil al invelisului - vezi
          `MaintenanceWindowsPage`, unde sta scris de ce nu dupa cartonas. */}
      <CmdbViews views={data.views} current="maintenance" />

      <AdmPageHead
        tone="green-mode"
        icon="fa-calendar-alt"
        title="Change calendar"
        note="what is planned, and what would run into it"
        stats={[
          { value: String(startingHere), label: 'planned this month', tone: 'grey' },
          { value: String(changesHere), label: 'changes this month', tone: 'grey' },
          {
            value: String(clashes.length + changeClashes.length),
            label: 'clashes',
            tone: clashes.length + changeClashes.length ? 'red-intense' : 'grey',
          },
        ]}
        actions={
          <>
            {data.canWrite && (
              <button type="button" className="btn btn-primary btn-sm" id="windowAdd" onClick={() => setForm({ id: 0, day: '' })}>
                <i className="fas fa-plus-circle"></i> Add a window
              </button>
            )}{' '}
            <a className="btn btn-default btn-sm" href="/app/cmdb/health#admChanges">
              <i className="fas fa-user-shield"></i> Unauthorised changes
            </a>
          </>
        }
      />

      <div className="adm-bar">
        <div className="adm-field">
          <label>Month</label>
          <div>
            <span className="btn-group">
              <a className="btn btn-sm btn-default" href={`/app/maintenance/calendar?month=${shiftMonth(data.month, -1)}`} title="Previous month">
                <i className="fas fa-chevron-left"></i>
              </a>
              <a className="btn btn-sm btn-default" href={`/app/maintenance/calendar?month=${shiftMonth(data.month, 1)}`} title="Next month">
                <i className="fas fa-chevron-right"></i>
              </a>
            </span>{' '}
            <strong>{monthLabel(data.month)}</strong>
          </div>
        </div>
        <div className="adm-spacer"></div>
        <div className="adm-field">
          <label>Shape</label>
          <MaintenanceShapes active="month" dayDate={dayDate} size="sm" />
        </div>
      </div>

      <div className="adm-section adm-section-green-mode">
        <h4>
          <i className="fas fa-calendar-alt"></i> {monthLabel(data.month)}
        </h4>
        <span className="adm-section-note">windows are marked when another one could reach the same item while they are open</span>
      </div>

      {clashes.length > 0 && (
        <div className="adm-card adm-tone-red-intense">
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-exclamation-triangle"></i> {clashes.length} clash{clashes.length === 1 ? '' : 'es'}
            </h5>
            <span className="adm-card-note">open at the same time, over the same items</span>
          </div>
          <table className="table adm-list">
            <tbody>
              {clashes.map((pair) => {
                const other = byId.get(pair.other.id)
                return (
                  <tr key={`${pair.one.ID}-${pair.other.id}`}>
                    <td>
                      <strong>{pair.one.name}</strong>
                      <div className="adm-muted" style={{ fontSize: 11 }}>
                        {pair.one.starts_at} &rarr; {pair.one.ends_at}
                      </div>
                    </td>
                    <td className="adm-muted" style={{ width: 30, textAlign: 'center' }}>
                      <i className="fas fa-times"></i>
                    </td>
                    <td>
                      <strong>{pair.other.name}</strong>
                      {other && (
                        <div className="adm-muted" style={{ fontSize: 11 }}>
                          {other.starts_at} &rarr; {other.ends_at}
                        </div>
                      )}
                    </td>
                    <td>{pair.other.reason}</td>
                    <td className="text-right adm-nowrap">
                      {data.canWrite && (
                        <button className="btn btn-default btn-xs windowEdit" onClick={() => setForm({ id: pair.one.ID, day: '' })}>
                          <i className="fa fa-pencil-alt"></i> Move
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {changeClashes.length > 0 && (
        <div className="adm-card adm-tone-red-intense od-change-clashes">
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-random"></i> {changeClashes.length} change clash{changeClashes.length === 1 ? '' : 'es'}
            </h5>
            <span className="adm-card-note">a change and another change or a window, over the same item</span>
          </div>
          <table className="table adm-list">
            <tbody>
              {changeClashes.map((clash) => {
                const one = changeById.get(clash.change)
                const other = clash.kind === 'change' ? changeById.get(clash.id) : undefined
                return (
                  <tr key={`${clash.change}-${clash.kind}-${clash.id}`}>
                    <td>
                      <a href={`/app/easeedesk/ticket/${clash.change}`}>
                        <strong>
                          #{clash.change} {one?.subject ?? ''}
                        </strong>
                      </a>
                      {one && (
                        <div className="adm-muted" style={{ fontSize: 11 }}>
                          {one.starts_at} &rarr; {one.ends_at}
                        </div>
                      )}
                    </td>
                    <td className="adm-muted" style={{ width: 30, textAlign: 'center' }}>
                      <i className="fas fa-times"></i>
                    </td>
                    <td>
                      {clash.kind === 'change' ? (
                        <a href={`/app/easeedesk/ticket/${clash.id}`}>
                          <strong>
                            #{clash.id} {clash.name}
                          </strong>
                        </a>
                      ) : (
                        <strong>
                          <i className="fas fa-calendar-check"></i> {clash.name}
                        </strong>
                      )}
                      {other && (
                        <div className="adm-muted" style={{ fontSize: 11 }}>
                          {other.starts_at} &rarr; {other.ends_at}
                        </div>
                      )}
                    </td>
                    <td>{clash.reason}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="adm-calendar">
        <div className="adm-calendar-head">
          {DAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="adm-calendar-grid">
          {Array.from({ length: data.cells }, (_, cell) => {
            const key = addDays(data.gridStart, cell)
            const onDay = byDay.get(key) ?? []
            const weekday = (new Date(`${key}T00:00:00`).getDay() + 6) % 7
            const classes = ['adm-day']
            if (key.slice(0, 7) !== data.month) classes.push('adm-day-outside')
            if (key === today) classes.push('adm-day-today')
            if (weekday >= 5) classes.push('adm-day-weekend')
            return (
              <div
                key={key}
                className={classes.join(' ')}
                data-day={data.canWrite ? key : undefined}
                onClick={(e) => {
                  if (!data.canWrite) return
                  if ((e.target as HTMLElement).closest('.adm-chip, .adm-day-number')) return
                  setForm({ id: 0, day: key })
                }}
              >
                <a className="adm-day-number" href={`/app/maintenance/day?date=${key}`} title={`See ${key} hour by hour`}>
                  {Number(key.slice(8, 10))}
                </a>
                {onDay.map((w) => {
                  const cl = data.conflicts[String(w.ID)] ?? []
                  const starts = w.starts_at_full.slice(0, 10) === key
                  let title = `${w.name} — ${w.scope_name} (${w.starts_at} → ${w.ends_at})`
                  if (cl.length) title += ` — clashes with ${cl.length}: ${cl[0].reason}`
                  return (
                    <span
                      key={w.ID}
                      className={`adm-chip adm-chip-${w.state} ${cl.length ? 'adm-chip-clash' : ''}`}
                      title={title}
                      data-id={w.ID}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (data.canWrite) setForm({ id: w.ID, day: '' })
                      }}
                    >
                      {cl.length > 0 ? <i className="fas fa-exclamation-triangle"></i> : starts ? <i className={`fas ${SCOPE_ICON[w.scope_type] ?? 'fa-cube'}`}></i> : null}
                      {starts && <span className="adm-chip-time">{w.starts_at_full.slice(11, 16)}</span>} {w.name}
                    </span>
                  )
                })}
                {(changesByDay.get(key) ?? []).map((c) => (
                  <ChangeChip key={`c${c.ticket_id}`} change={c} day={key} clash={clashingChanges.has(c.ticket_id)} />
                ))}
                {data.canWrite && onDay.length === 0 && (changesByDay.get(key) ?? []).length === 0 && (
                  <span className="adm-day-add" title="Plan a window on this day">
                    <i className="fas fa-plus"></i>
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="adm-calendar-legend">
        <span>
          <i className="adm-key adm-key-upcoming"></i> Upcoming
        </span>
        <span>
          <i className="adm-key adm-key-open"></i> Open now
        </span>
        <span>
          <i className="adm-key adm-key-past"></i> Finished
        </span>
        <span>
          <i className="adm-key adm-key-clash"></i> Could reach the same item as another
        </span>
        {(['draft', 'pending', 'approved', 'rejected'] as ApprovalState[]).map((st) => (
          <span key={st}>
            <i className="adm-key" style={{ background: APPROVAL_COLOR[st] }}></i> Change: {APPROVAL_LABEL[st].toLowerCase()}
          </span>
        ))}
      </div>

      <WindowFormModal
        open={form !== null}
        id={form?.id ?? 0}
        day={form?.day ?? ''}
        onClose={() => setForm(null)}
        onSaved={(message, warning) => toast(message, 'Maintenance windows', warning ? 'warning' : 'success')}
      />
    </Content>
  )
}

/**
 * Un change in celula zilei: culoarea e starea aprobarii (draft, asteapta CAB,
 * aprobat, respins), iar apasarea duce la fisa tichetului - acolo se aproba si
 * se muta, nu in calendar.
 */
function ChangeChip({ change, day, clash }: { change: CalendarChange; day: string; clash: boolean }) {
  const starts = change.starts_at_full.slice(0, 10) === day
  let title = `Change #${change.ticket_id} ${change.subject} — ${APPROVAL_LABEL[change.approval_state]} (${change.starts_at} → ${change.ends_at})`
  if (change.cis.length) title += ` — ${change.cis.join(', ')}`
  if (clash) title += ' — clashes with another change or a window'

  return (
    <a
      className={`adm-chip od-chip-change ${clash ? 'adm-chip-clash' : ''}`}
      href={`/app/easeedesk/ticket/${change.ticket_id}`}
      title={title}
      data-change={change.ticket_id}
      data-state={change.approval_state}
      style={{ background: APPROVAL_COLOR[change.approval_state], color: '#fff' }}
      onClick={(e) => e.stopPropagation()}
    >
      {clash ? <i className="fas fa-exclamation-triangle"></i> : <i className="fas fa-random"></i>}
      {starts && <span className="adm-chip-time">{change.starts_at_full.slice(11, 16)}</span>} #{change.ticket_id} {change.subject}
    </a>
  )
}
